<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Trustees are individuals or entities responsible for managing the trust's
          assets and carrying out the terms of the trust agreement.
        </p>
      </div>
    </help-drawer>
    <v-form name="Trustees" class="form" data-test-id="trustees-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.TRUSTEES.id" :instance="instance"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="trustees-header">
              <span class="headerlight">
                <strong>Trustee information</strong> for
                <strong>{{ label }}</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving trustee information...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isTrusteesComplete && formHidden && !isSyncing"
          :submitted="isTrusteesComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-row class="justify-center" v-if="!formHidden && !isSyncing">
          <v-col class="step-form-container large">
            
            <!-- Are you the trustee? -->
            <div class="text-center mb-6">
              <label class="required mb-2 d-block" data-test-id="interviewer-trustee-label">
                Are you the trustee?
              </label>
              <div class="d-flex justify-center">
                <v-radio-group
                  v-model="interviewerIsTrustee"
                  :error-messages="interviewerIsTrusteeErrors"
                  hide-details="auto"
                  row
                  @change="handleInterviewerChange"
                  data-test-id="interviewer-trustee-radio-group"
                  class="trustee-toggle"
                >
                  <v-radio
                    :ripple="false"
                    class="v-faux-radio half-width first"
                    label="Yes"
                    :value="true"
                    data-test-id="interviewer-trustee-yes"
                  ></v-radio>
                  <v-radio
                    :ripple="false"
                    class="v-faux-radio half-width"
                    label="No"
                    :value="false"
                    data-test-id="interviewer-trustee-no"
                  ></v-radio>
                </v-radio-group>
              </div>
            </div>
            
            <!-- Trustee Details -->
            <v-expand-transition>
              <div v-if="interviewerIsTrustee === false || (interviewerIsTrustee === true && trustees.length > 0)" class="mt-4">
                <div v-for="(trustee, index) in trustees" :key="index" class="mb-4">
                  <v-card class="pa-4" elevation="0" outlined>
                    <h3 class="mb-4">Trustee {{ interviewerIsTrustee === true ? index + 2 : index + 1 }}</h3>
                    
                    <!-- Basic Information -->
                    <h4 class="mb-3">Basic Information</h4>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <label :for="`firstName-${index}`" class="required" data-test-id="first-name-label">
                          First Name
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          :name="`firstName-${index}`"
                          aria-required="true"
                          v-model.trim="trustee.firstName"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.trustees.$each[index].firstName.$touch()"
                          @blur="$v.trustees.$each[index].firstName.$touch()"
                          :error-messages="getFirstNameErrors(index)"
                          hide-details="auto"
                          :data-test-id="`first-name-input-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <label :for="`lastName-${index}`" class="required" data-test-id="last-name-label">
                          Last Name
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          :name="`lastName-${index}`"
                          aria-required="true"
                          v-model.trim="trustee.lastName"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.trustees.$each[index].lastName.$touch()"
                          @blur="$v.trustees.$each[index].lastName.$touch()"
                          :error-messages="getLastNameErrors(index)"
                          hide-details="auto"
                          :data-test-id="`last-name-input-${index}`"
                        />
                      </v-col>
                    </v-row>
                
                    <!-- Contact Information -->
                    <v-row class="mt-2">
                      <v-col cols="12" sm="3">
                        <label :for="`email-${index}`" class="required" data-test-id="email-label">
                          Email
                        </label>
                        <v-text-field
                          outlined
                          type="email"
                          maxlength="256"
                          :name="`email-${index}`"
                          aria-required="true"
                          v-model.trim="trustee.email"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.trustees.$each[index].email.$touch()"
                          @blur="$v.trustees.$each[index].email.$touch()"
                          :error-messages="getEmailErrors(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`email-input-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`phone-${index}`" data-test-id="phone-label">
                          Phone
                        </label>
                        <v-text-field
                          outlined
                          type="tel"
                          maxlength="14"
                          :name="`phone-${index}`"
                          v-model="trustee.phone"
                          placeholder="###-###-####"
                          :backgroundColor="inputBackgroundColor"
                          @input="formatPhone(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`phone-input-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`dob-${index}`" class="required" data-test-id="dob-label">
                          Date of Birth
                        </label>
                        <v-text-field
                          v-model="trustee.dob"
                          :placeholder="INPUT_DATE_FMT"
                          outlined
                          :name="`dob-${index}`"
                          maxlength="8"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.trustees.$each[index].dob.$touch()"
                          @blur="standardizeDob(index)"
                          :error-messages="getDobErrors(index)"
                          hide-details="auto"
                          inputmode="numeric"
                          dense
                          :data-test-id="`dob-input-${index}`"
                        >
                          <template v-slot:append>
                            <v-icon small color="#9b9b9b">
                              fas fa-calendar-alt
                            </v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`ssn-${index}`" class="required" data-test-id="ssn-label">
                          SSN / Tax ID
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="11"
                          :name="`ssn-${index}`"
                          aria-required="true"
                          v-model="trustee.ssn"
                          placeholder="###-##-####"
                          :backgroundColor="inputBackgroundColor"
                          @input="formatSsn(index)"
                          @blur="$v.trustees.$each[index].ssn.$touch()"
                          :error-messages="getSsnErrors(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`ssn-input-${index}`"
                        />
                      </v-col>
                    </v-row>
                    
                    <!-- Personal Information -->
                    <h4 class="mt-4 mb-3">Personal Information</h4>
                    <v-row>
                      <v-col cols="12" sm="3">
                        <label :for="`secondaryPhone-${index}`" data-test-id="secondary-phone-label">
                          Secondary Phone
                        </label>
                        <v-text-field
                          outlined
                          type="tel"
                          maxlength="14"
                          :name="`secondaryPhone-${index}`"
                          v-model="trustee.secondaryPhone"
                          placeholder="###-###-####"
                          :backgroundColor="inputBackgroundColor"
                          @input="formatSecondaryPhone(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`secondary-phone-input-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label class="required mb-2" data-test-id="us-resident-label">
                          US Permanent Resident?
                        </label>
                        <v-radio-group
                          v-model="trustee.usPermanentResident"
                          :error-messages="getUsPermanentResidentErrors(index)"
                          hide-details="auto"
                          row
                          @change="$v.trustees.$each[index].usPermanentResident.$touch()"
                          :data-test-id="`us-resident-radio-group-${index}`"
                          class="mt-1"
                        >
                          <v-radio
                            :ripple="false"
                            label="Yes"
                            :value="true"
                            :data-test-id="`us-resident-yes-${index}`"
                          ></v-radio>
                          <v-radio
                            :ripple="false"
                            label="No"
                            :value="false"
                            :data-test-id="`us-resident-no-${index}`"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label class="required mb-2" data-test-id="ten-percent-label">
                          10% Shareholder?
                        </label>
                        <v-radio-group
                          v-model="trustee.tenPercentShareholder"
                          :error-messages="getTenPercentShareholderErrors(index)"
                          hide-details="auto"
                          row
                          @change="$v.trustees.$each[index].tenPercentShareholder.$touch()"
                          :data-test-id="`ten-percent-radio-group-${index}`"
                          class="mt-1"
                        >
                          <v-radio
                            :ripple="false"
                            label="Yes"
                            :value="true"
                            :data-test-id="`ten-percent-yes-${index}`"
                          ></v-radio>
                          <v-radio
                            :ripple="false"
                            label="No"
                            :value="false"
                            :data-test-id="`ten-percent-no-${index}`"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label class="required mb-2" data-test-id="finra-member-label">
                          FINRA Member?
                        </label>
                        <v-radio-group
                          v-model="trustee.finraMember"
                          :error-messages="getFinraMemberErrors(index)"
                          hide-details="auto"
                          row
                          @change="$v.trustees.$each[index].finraMember.$touch()"
                          :data-test-id="`finra-member-radio-group-${index}`"
                          class="mt-1"
                        >
                          <v-radio
                            :ripple="false"
                            label="Yes"
                            :value="true"
                            :data-test-id="`finra-member-yes-${index}`"
                          ></v-radio>
                          <v-radio
                            :ripple="false"
                            label="No"
                            :value="false"
                            :data-test-id="`finra-member-no-${index}`"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                    
                    <!-- Financial & Background Information -->
                    <v-row class="mt-2">
                      <v-col cols="12" sm="3">
                        <label class="required mb-2" data-test-id="politically-exposed-label">
                          Politically Exposed?
                        </label>
                        <v-radio-group
                          v-model="trustee.politicallyExposed"
                          :error-messages="getPoliticallyExposedErrors(index)"
                          hide-details="auto"
                          row
                          @change="$v.trustees.$each[index].politicallyExposed.$touch()"
                          :data-test-id="`politically-exposed-radio-group-${index}`"
                          class="mt-1"
                        >
                          <v-radio
                            :ripple="false"
                            label="Yes"
                            :value="true"
                            :data-test-id="`politically-exposed-yes-${index}`"
                          ></v-radio>
                          <v-radio
                            :ripple="false"
                            label="No"
                            :value="false"
                            :data-test-id="`politically-exposed-no-${index}`"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`citizenship-${index}`" class="required" data-test-id="citizenship-label">
                          Citizenship
                        </label>
                        <v-select
                          v-model="trustee.citizenship"
                          :items="countries"
                          :backgroundColor="inputBackgroundColor"
                          outlined
                          dense
                          hide-details="auto"
                          @change="$v.trustees.$each[index].citizenship.$touch()"
                          :error-messages="getCitizenshipErrors(index)"
                          :data-test-id="`citizenship-select-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`sourceOfWealth-${index}`" class="required" data-test-id="source-wealth-label">
                          Source of Wealth
                        </label>
                        <v-select
                          v-model="trustee.sourceOfWealth"
                          :items="wealthSources"
                          :backgroundColor="inputBackgroundColor"
                          outlined
                          dense
                          hide-details="auto"
                          @change="$v.trustees.$each[index].sourceOfWealth.$touch()"
                          :error-messages="getSourceOfWealthErrors(index)"
                          :data-test-id="`source-wealth-select-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`annualIncome-${index}`" class="required" data-test-id="annual-income-label">
                          Annual Income
                        </label>
                        <v-text-field
                          outlined
                          type="number"
                          :name="`annualIncome-${index}`"
                          aria-required="true"
                          v-model.number="trustee.annualIncome"
                          prefix="$"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.trustees.$each[index].annualIncome.$touch()"
                          @blur="$v.trustees.$each[index].annualIncome.$touch()"
                          :error-messages="getAnnualIncomeErrors(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`annual-income-input-${index}`"
                        />
                      </v-col>
                    </v-row>
                    
                    <!-- Employment Information -->
                    <h4 class="mt-4 mb-3">Employment Information</h4>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <label :for="`employmentStatus-${index}`" class="required" data-test-id="employment-status-label">
                          Employment Status
                        </label>
                        <v-select
                          v-model="trustee.employmentStatus"
                          :items="employmentStatuses"
                          :backgroundColor="inputBackgroundColor"
                          outlined
                          dense
                          hide-details="auto"
                          @change="handleEmploymentStatusChange(index)"
                          :error-messages="getEmploymentStatusErrors(index)"
                          :data-test-id="`employment-status-select-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" v-if="isEmployed(trustee.employmentStatus)">
                        <label :for="`employerDetails-${index}`" class="required" data-test-id="employer-details-label">
                          Employer Name
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          :name="`employerDetails-${index}`"
                          aria-required="true"
                          v-model.trim="trustee.employerDetails"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.trustees.$each[index].employerDetails.$touch()"
                          @blur="$v.trustees.$each[index].employerDetails.$touch()"
                          :error-messages="getEmployerDetailsErrors(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`employer-details-input-${index}`"
                        />
                      </v-col>
                    </v-row>
                    <v-row v-if="isEmployed(trustee.employmentStatus)">
                      <v-col cols="12" sm="6">
                        <label :for="`employerIndustry-${index}`" class="required" data-test-id="employer-industry-label">
                          Line of Business
                        </label>
                        <v-select
                          v-model="trustee.employerIndustry"
                          :items="businessLines"
                          :backgroundColor="inputBackgroundColor"
                          outlined
                          dense
                          hide-details="auto"
                          @change="$v.trustees.$each[index].employerIndustry.$touch()"
                          :error-messages="getEmployerIndustryErrors(index)"
                          :data-test-id="`employer-industry-select-${index}`"
                        />
                      </v-col>
                    </v-row>
                
                    <!-- Use Same Address Toggle -->
                    <v-row class="mt-4">
                      <v-col cols="12">
                        <label class="required mb-2" data-test-id="use-same-address-label">
                          Use same address as account holder?
                        </label>
                        <v-radio-group
                          v-model="trustee.useSameAddress"
                          :error-messages="getUseSameAddressErrors(index)"
                          hide-details="auto"
                          row
                          @change="$v.trustees.$each[index].useSameAddress.$touch()"
                          :data-test-id="`use-same-address-radio-group-${index}`"
                        >
                          <v-radio
                            :ripple="false"
                            class="v-faux-radio half-width first"
                            label="Yes"
                            :value="true"
                            :data-test-id="`use-same-address-yes-${index}`"
                          ></v-radio>
                          <v-radio
                            :ripple="false"
                            class="v-faux-radio half-width"
                            label="No"
                            :value="false"
                            :data-test-id="`use-same-address-no-${index}`"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                
                    <!-- Address (conditional) -->
                    <v-expand-transition>
                      <div v-if="trustee.useSameAddress === false" class="mt-4">
                        <h4 class="mb-3">Trustee Address</h4>
                        <label :for="`street-${index}`" class="required" data-test-id="street-label">
                          Street Address
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          :name="`street-${index}`"
                          aria-required="true"
                          v-model.trim="trustee.address.street"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.trustees.$each[index].address.street.$touch()"
                          @blur="$v.trustees.$each[index].address.street.$touch()"
                          :error-messages="getAddressStreetErrors(index)"
                          hide-details="auto"
                          class="mb-2"
                          :data-test-id="`street-input-${index}`"
                        />
                        
                        <v-row class="mt-2">
                          <v-col cols="12" sm="6">
                            <label :for="`city-${index}`" class="required" data-test-id="city-label">
                              City
                            </label>
                            <v-text-field
                              outlined
                              type="text"
                              maxlength="256"
                              :name="`city-${index}`"
                              aria-required="true"
                              v-model.trim="trustee.address.city"
                              :backgroundColor="inputBackgroundColor"
                              @input="$v.trustees.$each[index].address.city.$touch()"
                              @blur="$v.trustees.$each[index].address.city.$touch()"
                              :error-messages="getAddressCityErrors(index)"
                              hide-details="auto"
                              :data-test-id="`city-input-${index}`"
                            />
                          </v-col>
                          <v-col cols="12" sm="3">
                            <label :for="`state-${index}`" class="required" data-test-id="state-label">
                              State
                            </label>
                            <v-select
                              v-model="trustee.address.state"
                              :items="states"
                              :backgroundColor="inputBackgroundColor"
                              outlined
                              dense
                              hide-details="auto"
                              @change="$v.trustees.$each[index].address.state.$touch()"
                              :error-messages="getAddressStateErrors(index)"
                              :data-test-id="`state-select-${index}`"
                            />
                          </v-col>
                          <v-col cols="12" sm="3">
                            <label :for="`zipCode-${index}`" class="required" data-test-id="zip-label">
                              ZIP Code
                            </label>
                            <v-text-field
                              outlined
                              type="text"
                              maxlength="10"
                              :name="`zipCode-${index}`"
                              aria-required="true"
                              v-model="trustee.address.zipCode"
                              :backgroundColor="inputBackgroundColor"
                              @input="formatZipCode(index)"
                              @blur="$v.trustees.$each[index].address.zipCode.$touch()"
                              :error-messages="getAddressZipErrors(index)"
                              hide-details="auto"
                              :data-test-id="`zip-input-${index}`"
                            />
                          </v-col>
                        </v-row>
                        
                        <v-row class="mt-2">
                          <v-col cols="12" sm="6">
                            <label :for="`country-${index}`" class="required" data-test-id="country-label">
                              Country
                            </label>
                            <v-select
                              v-model="trustee.address.country"
                              :items="countries"
                              :backgroundColor="inputBackgroundColor"
                              outlined
                              dense
                              hide-details="auto"
                              @change="$v.trustees.$each[index].address.country.$touch()"
                              :error-messages="getAddressCountryErrors(index)"
                              :data-test-id="`country-select-${index}`"
                            />
                          </v-col>
                        </v-row>
                      </div>
                    </v-expand-transition>
                
                    <!-- Remove Trustee Button -->
                    <v-row v-if="(interviewerIsTrustee === false && index === 1) || (interviewerIsTrustee === true && index === 0)" class="mt-3">
                      <v-col cols="12" class="text-left">
                        <v-btn
                          outlined
                          color="error"
                          @click="removeTrustee(index)"
                          data-test-id="remove-trustee-button"
                        >
                          <v-icon left small>mdi-delete</v-icon>
                          Remove Trustee
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card>
                </div>
                
                <!-- Add Another Trustee Button -->
                <v-row 
                  v-if="interviewerIsTrustee === false && trustees.length < 2"
                  class="mt-4 mb-4 justify-center text-center"
                >
                  <v-col cols="12">
                    <v-btn
                      outlined
                      color="primary"
                      @click="addTrustee"
                      data-test-id="add-trustee-button"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Another Trustee
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>

            <!-- Add-trustee button when no co-trustee has been added yet (interviewer is trustee) -->
            <v-row 
              v-if="interviewerIsTrustee === true && trustees.length < 1"
              class="mt-4 mb-4 justify-center text-center"
            >
              <v-col cols="12">
                <v-btn
                  outlined
                  color="primary"
                  @click="addTrustee"
                  data-test-id="add-trustee-button"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Another Trustee
                </v-btn>
              </v-col>
            </v-row>
            
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="trustees-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS, STATES, COUNTRIES, WEALTH_SOURCE_LIST, BUSINESS_LINE_LIST, CLIENT_EMPLOYMENT_STATUSES, INPUT_DATE_FMT, validInputDate, validateZip } from '@/common/constants';
import { required, requiredIf, email, numeric } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

const createEmptyTrustee = () => ({
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  secondaryPhone: null,
  useSameAddress: null,
  address: {
    street: null,
    city: null,
    state: null,
    zipCode: null,
    country: 'US',
  },
  dob: null,
  ssn: null,
  citizenship: null,
  usPermanentResident: null,
  tenPercentShareholder: null,
  finraMember: null,
  politicallyExposed: null,
  sourceOfWealth: null,
  employmentStatus: null,
  employerDetails: null,
  employerIndustry: null,
  annualIncome: null,
});

export default {
  name: 'Trustees',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],
  
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    interviewerIsTrustee: null,
    trustees: [createEmptyTrustee()],
    employmentStatuses: Object.values(CLIENT_EMPLOYMENT_STATUSES),
    states: STATES,
    countries: COUNTRIES,
    wealthSources: Object.values(WEALTH_SOURCE_LIST),
    businessLines: Object.values(BUSINESS_LINE_LIST),
    MENUS: MENUS,
    INPUT_DATE_FMT: INPUT_DATE_FMT,
  }),
  
  validations() {
    const validations = {
      interviewerIsTrustee: { required },
      trustees: {
        $each: {
          firstName: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            })
          },
          lastName: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            })
          },
          email: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            }),
            email: email,
          },
          phone: {},
          secondaryPhone: {},
          dob: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            }),
            validInputDate,
          },
          ssn: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            }),
            validSsn(value) {
              if (!value) return true;
              const cleaned = value.replace(/[^0-9]/g, '');
              return cleaned.length === 9;
            }
          },
          usPermanentResident: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            })
          },
          tenPercentShareholder: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            })
          },
          finraMember: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            })
          },
          politicallyExposed: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            })
          },
          useSameAddress: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            })
          },
          address: {
            street: { 
              required: requiredIf(function() {
                const trustee = this.trustees[this.$v.trustees.$each.$iter];
                return this.interviewerIsTrustee === false && trustee && !trustee.useSameAddress;
              })
            },
            city: { 
              required: requiredIf(function() {
                const trustee = this.trustees[this.$v.trustees.$each.$iter];
                return this.interviewerIsTrustee === false && trustee && !trustee.useSameAddress;
              })
            },
            state: { 
              required: requiredIf(function() {
                const trustee = this.trustees[this.$v.trustees.$each.$iter];
                return this.interviewerIsTrustee === false && trustee && !trustee.useSameAddress;
              })
            },
            zipCode: { 
              required: requiredIf(function() {
                const trustee = this.trustees[this.$v.trustees.$each.$iter];
                return this.interviewerIsTrustee === false && trustee && !trustee.useSameAddress;
              }),
              validateZip
            },
            country: { 
              required: requiredIf(function() {
                const trustee = this.trustees[this.$v.trustees.$each.$iter];
                return this.interviewerIsTrustee === false && trustee && !trustee.useSameAddress;
              })
            },
          },
          citizenship: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            })
          },
          sourceOfWealth: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            })
          },
          employmentStatus: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            })
          },
          employerDetails: { 
            required: requiredIf(function() {
              const trustee = this.trustees[this.$v.trustees.$each.$iter];
              return this.interviewerIsTrustee === false && trustee && this.isEmployed(trustee.employmentStatus);
            })
          },
          employerIndustry: { 
            required: requiredIf(function() {
              const trustee = this.trustees[this.$v.trustees.$each.$iter];
              return this.interviewerIsTrustee === false && trustee && this.isEmployed(trustee.employmentStatus);
            })
          },
          annualIncome: { 
            required: requiredIf(function() {
              return this.interviewerIsTrustee === false;
            }),
            numeric,
          },
        }
      },
      validationGroup: ['interviewerIsTrustee', 'trustees'],
    };
    
    return validations;
  },
  
  computed: {
    instance() {
      return {
        name: this.name,
        label: this.label,
      };
    },
    
    isTrusteesComplete() {
      const page = this.getPage(MENUS.CLIENT.TRUSTEES.id, this.instance);
      return page && page.lastCompleted;
    },
    
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.TRUSTEES.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    
    helpTitle() {
      return 'What does this mean?';
    },
    
    interviewerIsTrusteeErrors() {
      const errors = [];
      if (!this.$v.interviewerIsTrustee.$dirty) return errors;
      !this.$v.interviewerIsTrustee.required && errors.push('This field is required');
      return errors;
    },
  },
  
  methods: {
    isEmployed(status) {
      return status === CLIENT_EMPLOYMENT_STATUSES.FULL_TIME_EMPLOYED ||
             status === CLIENT_EMPLOYMENT_STATUSES.PART_TIME_EMPLOYED ||
             status === CLIENT_EMPLOYMENT_STATUSES.SELF_EMPLOYED;
    },
    
    // Error getters for dynamic validation
    getFirstNameErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].firstName.$dirty) return errors;
      !this.$v.trustees.$each[index].firstName.required && errors.push('First name is required');
      return errors;
    },
    
    getLastNameErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].lastName.$dirty) return errors;
      !this.$v.trustees.$each[index].lastName.required && errors.push('Last name is required');
      return errors;
    },
    
    getEmailErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].email.$dirty) return errors;
      !this.$v.trustees.$each[index].email.required && errors.push('Email is required');
      !this.$v.trustees.$each[index].email.email && errors.push('Please enter a valid email');
      return errors;
    },
    
    getDobErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].dob.$dirty) return errors;
      !this.$v.trustees.$each[index].dob.required && errors.push('Date of birth is required');
      !this.$v.trustees.$each[index].dob.validInputDate && errors.push('Please enter a valid date');
      return errors;
    },
    
    getSsnErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].ssn.$dirty) return errors;
      !this.$v.trustees.$each[index].ssn.required && errors.push('SSN is required');
      !this.$v.trustees.$each[index].ssn.validSsn && errors.push('Please enter a valid SSN');
      return errors;
    },
    
    getUsPermanentResidentErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].usPermanentResident.$dirty) return errors;
      !this.$v.trustees.$each[index].usPermanentResident.required && errors.push('This field is required');
      return errors;
    },
    
    getTenPercentShareholderErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].tenPercentShareholder.$dirty) return errors;
      !this.$v.trustees.$each[index].tenPercentShareholder.required && errors.push('This field is required');
      return errors;
    },
    
    getFinraMemberErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].finraMember.$dirty) return errors;
      !this.$v.trustees.$each[index].finraMember.required && errors.push('This field is required');
      return errors;
    },
    
    getPoliticallyExposedErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].politicallyExposed.$dirty) return errors;
      !this.$v.trustees.$each[index].politicallyExposed.required && errors.push('This field is required');
      return errors;
    },
    
    getCitizenshipErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].citizenship.$dirty) return errors;
      !this.$v.trustees.$each[index].citizenship.required && errors.push('Citizenship is required');
      return errors;
    },
    
    getSourceOfWealthErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].sourceOfWealth.$dirty) return errors;
      !this.$v.trustees.$each[index].sourceOfWealth.required && errors.push('Source of wealth is required');
      return errors;
    },
    
    getEmploymentStatusErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].employmentStatus.$dirty) return errors;
      !this.$v.trustees.$each[index].employmentStatus.required && errors.push('Employment status is required');
      return errors;
    },
    
    getEmployerDetailsErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].employerDetails.$dirty) return errors;
      !this.$v.trustees.$each[index].employerDetails.required && errors.push('Employer name is required');
      return errors;
    },
    
    getEmployerIndustryErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].employerIndustry.$dirty) return errors;
      !this.$v.trustees.$each[index].employerIndustry.required && errors.push('Line of business is required');
      return errors;
    },
    
    getAnnualIncomeErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].annualIncome.$dirty) return errors;
      !this.$v.trustees.$each[index].annualIncome.required && errors.push('Annual income is required');
      !this.$v.trustees.$each[index].annualIncome.numeric && errors.push('Please enter a valid number');
      return errors;
    },
    
    
    getUseSameAddressErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].useSameAddress.$dirty) return errors;
      !this.$v.trustees.$each[index].useSameAddress.required && errors.push('This field is required');
      return errors;
    },
    
    getAddressStreetErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].address.street.$dirty) return errors;
      !this.$v.trustees.$each[index].address.street.required && errors.push('Street address is required');
      return errors;
    },
    
    getAddressCityErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].address.city.$dirty) return errors;
      !this.$v.trustees.$each[index].address.city.required && errors.push('City is required');
      return errors;
    },
    
    getAddressStateErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].address.state.$dirty) return errors;
      !this.$v.trustees.$each[index].address.state.required && errors.push('State is required');
      return errors;
    },
    
    getAddressZipErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].address.zipCode.$dirty) return errors;
      !this.$v.trustees.$each[index].address.zipCode.required && errors.push('ZIP code is required');
      !this.$v.trustees.$each[index].address.zipCode.validateZip && errors.push('ZIP code must be NNNNN or NNNNN-NNNN');
      return errors;
    },
    
    getAddressCountryErrors(index) {
      const errors = [];
      if (!this.$v.trustees.$each[index]) return errors;
      if (!this.$v.trustees.$each[index].address.country.$dirty) return errors;
      !this.$v.trustees.$each[index].address.country.required && errors.push('Country is required');
      return errors;
    },
    
    handleInterviewerChange() {
      this.$v.interviewerIsTrustee.$touch();

      if (this.interviewerIsTrustee === true) {
        // Clear trustees data so the user can explicitly add co-trustee(s)
        this.trustees = [];
        this.$v.trustees.$reset();
      } else if (this.interviewerIsTrustee === false) {
        // Ensure at least one trustee card is present when interviewer is NOT the trustee
        if (this.trustees.length === 0) {
          this.trustees.push(createEmptyTrustee());
        }
      }
    },
    
    addTrustee() {
      const limit = this.interviewerIsTrustee === true ? 1 : 2;
      if (this.trustees.length < limit) {
        this.trustees.push(createEmptyTrustee());
      }
    },
    
    removeTrustee(index) {
      if (this.interviewerIsTrustee === true) {
        // interviewer is trustee, only one co-trustee allowed at index 0
        if (index === 0 && this.trustees.length === 1) {
          this.trustees.splice(index, 1);
        }
      } else {
        // normal path – allow removing the second trustee (index 1)
        if (index === 1 && this.trustees.length === 2) {
          this.trustees.splice(index, 1);
        }
      }
    },
    
    formatPhone(index) {
      let value = this.trustees[index].phone;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ###-###-####
      if (value.length > 3 && value.length <= 6) {
        value = value.slice(0, 3) + '-' + value.slice(3);
      } else if (value.length > 6) {
        value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
      }
      
      this.trustees[index].phone = value;
    },
    
    formatSecondaryPhone(index) {
      let value = this.trustees[index].secondaryPhone;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ###-###-####
      if (value.length > 3 && value.length <= 6) {
        value = value.slice(0, 3) + '-' + value.slice(3);
      } else if (value.length > 6) {
        value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
      }
      
      this.trustees[index].secondaryPhone = value;
    },
    
    formatSsn(index) {
      let value = this.trustees[index].ssn;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ###-##-####
      if (value.length > 3 && value.length <= 5) {
        value = value.slice(0, 3) + '-' + value.slice(3);
      } else if (value.length > 5) {
        value = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5, 9);
      }
      
      this.trustees[index].ssn = value;
      this.$v.trustees.$each[index].ssn.$touch();
    },
    
    standardizeDob(index) {
      if (!this.trustees[index].dob) return;
      
      let date = this.trustees[index].dob.replace(/[^0-9]/g, '');
      if (date.length === 8) {
        // Format as MM/DD/YYYY
        date = date.slice(0, 2) + '/' + date.slice(2, 4) + '/' + date.slice(4, 8);
        this.trustees[index].dob = date;
      }
    },
    
    handleEmploymentStatusChange(index) {
      this.$v.trustees.$each[index].employmentStatus.$touch();
      
      // Clear employment fields if not employed
      if (!this.isEmployed(this.trustees[index].employmentStatus)) {
        this.trustees[index].employerDetails = null;
        this.trustees[index].employerIndustry = null;
      }
    },
    
    formatZipCode(index) {
      let value = this.trustees[index].address.zipCode;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ##### or #####-####
      if (value.length > 5) {
        value = value.slice(0, 5) + '-' + value.slice(5, 9);
      }
      
      this.trustees[index].address.zipCode = value;
      this.$v.trustees.$each[index].address.zipCode.$touch();
    },
    
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        instance: this.instance,
        interviewerIsTrustee: this.interviewerIsTrustee,
      };
      
      if (this.interviewerIsTrustee === true) {
        // If interviewer is trustee, send trustee1 with isInterviewer: true
        data.trustee1 = {
          isInterviewer: true,
        };

        // If a co-trustee was added, include as trustee2
        if (this.trustees[0]) {
          const t = this.trustees[0];
          const trustee2Data = {
            isInterviewer: false,
            firstName: t.firstName,
            lastName: t.lastName,
            email: t.email,
            phone: t.phone,
            secondaryPhone: t.secondaryPhone,
            citizenship: t.citizenship,
            usPermanentResident: t.usPermanentResident,
            tenPercentShareholder: t.tenPercentShareholder,
            finraMember: t.finraMember,
            politicallyExposed: t.politicallyExposed,
            sourceOfWealth: t.sourceOfWealth,
            employmentStatus: t.employmentStatus,
            employerDetails: t.employerDetails,
            employerIndustry: t.employerIndustry,
            annualIncome: t.annualIncome,
            ssn: t.ssn,
          };

          // Convert DOB to ISO format
          if (t.dob) {
            const dp = t.dob.split('/');
            if (dp.length === 3) {
              trustee2Data.dob = `${dp[2]}-${dp[0].padStart(2,'0')}-${dp[1].padStart(2,'0')}`;
            }
          }

          // Only include address if useSameAddress is false
          if (!t.useSameAddress) {
            trustee2Data.address = {
              line1: t.address.street,
              city: t.address.city,
              state: t.address.state,
              zip: t.address.zipCode,
              country: t.address.country,
            };
          }

          data.trustee2 = trustee2Data;
        }
      } else {
        // Map trustees array to trustee1 and trustee2
        if (this.trustees[0]) {
          const trustee1Data = {
            isInterviewer: false,
            firstName: this.trustees[0].firstName,
            lastName: this.trustees[0].lastName,
            email: this.trustees[0].email,
            phone: this.trustees[0].phone,
            secondaryPhone: this.trustees[0].secondaryPhone,
            citizenship: this.trustees[0].citizenship,
            usPermanentResident: this.trustees[0].usPermanentResident,
            tenPercentShareholder: this.trustees[0].tenPercentShareholder,
            finraMember: this.trustees[0].finraMember,
            politicallyExposed: this.trustees[0].politicallyExposed,
            sourceOfWealth: this.trustees[0].sourceOfWealth,
            employmentStatus: this.trustees[0].employmentStatus,
            employerDetails: this.trustees[0].employerDetails,
            employerIndustry: this.trustees[0].employerIndustry,
            annualIncome: this.trustees[0].annualIncome,
            ssn: this.trustees[0].ssn,
          };
          
          // Convert DOB to ISO format
          if (this.trustees[0].dob) {
            const dateParts = this.trustees[0].dob.split('/');
            if (dateParts.length === 3) {
              trustee1Data.dob = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
            }
          }
          
          // Only include address if useSameAddress is false
          if (!this.trustees[0].useSameAddress) {
            trustee1Data.address = {
              line1: this.trustees[0].address.street,
              city: this.trustees[0].address.city,
              state: this.trustees[0].address.state,
              zip: this.trustees[0].address.zipCode,
              country: this.trustees[0].address.country,
            };
          }
          
          data.trustee1 = trustee1Data;
        }
        
        if (this.trustees[1]) {
          const trustee2Data = {
            isInterviewer: false,
            firstName: this.trustees[1].firstName,
            lastName: this.trustees[1].lastName,
            email: this.trustees[1].email,
            phone: this.trustees[1].phone,
            secondaryPhone: this.trustees[1].secondaryPhone,
            citizenship: this.trustees[1].citizenship,
            usPermanentResident: this.trustees[1].usPermanentResident,
            tenPercentShareholder: this.trustees[1].tenPercentShareholder,
            finraMember: this.trustees[1].finraMember,
            politicallyExposed: this.trustees[1].politicallyExposed,
            sourceOfWealth: this.trustees[1].sourceOfWealth,
            employmentStatus: this.trustees[1].employmentStatus,
            employerDetails: this.trustees[1].employerDetails,
            employerIndustry: this.trustees[1].employerIndustry,
            annualIncome: this.trustees[1].annualIncome,
            ssn: this.trustees[1].ssn,
          };
          
          // Convert DOB to ISO format
          if (this.trustees[1].dob) {
            const dateParts = this.trustees[1].dob.split('/');
            if (dateParts.length === 3) {
              trustee2Data.dob = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
            }
          }
          
          // Only include address if useSameAddress is false
          if (!this.trustees[1].useSameAddress) {
            trustee2Data.address = {
              line1: this.trustees[1].address.street,
              city: this.trustees[1].address.city,
              state: this.trustees[1].address.state,
              zip: this.trustees[1].address.zipCode,
              country: this.trustees[1].address.country,
            };
          }
          
          data.trustee2 = trustee2Data;
        }
      }

      this.markPageAsSyncing(MENUS.CLIENT.TRUSTEES.id, this.instance);

      this.completeStep(MENUS.CLIENT.TRUSTEES.id, data, this.instance)
        .then(() => {
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    skip() {
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    toggleFormState(hide) {
      this.formHidden = hide;
      
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    handleSyncStatusChange() {
      if (this.isTrusteesComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    
    isTrusteesComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    // Get the current page index based on name and instance
    this.pages = this.interviewPages.getInterviewPages();
    
    // Find the index of this specific trustees page
    this.pageIndex = this.pages.findIndex(page => {
      if (typeof page === 'object' && page.name === MENUS.CLIENT.TRUSTEES.id) {
        return page.params && page.params.name === this.name && page.params.label === this.label;
      }
      return false;
    });

    this.formHidden = this.isTrusteesComplete;
    
    // Load existing data if available
    const existingPage = this.getPage(MENUS.CLIENT.TRUSTEES.id, this.instance);
    if (existingPage && existingPage.data) {
      if (existingPage.data.interviewerIsTrustee !== undefined) {
        this.interviewerIsTrustee = existingPage.data.interviewerIsTrustee;
      }
      if (existingPage.data.trustees) {
        this.trustees = existingPage.data.trustees;
      }
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      if (this.isTrusteesComplete) {
        this.formHidden = true;
      }
    });
    
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>

<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}

.trustee-toggle {
  margin: 0 auto;
  display: inline-flex !important;
  
  ::v-deep .v-input__control {
    justify-content: center;
  }
}

h4 {
  font-size: 1.1rem;
  font-weight: 500;
  color: #424242;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.trustee-toggle {
  .v-input__control {
    width: auto !important;
  }
  
  .v-input__slot {
    margin: 0 auto;
  }
}
</style>